import { useEffect, useMemo, useRef, useState } from "react";

import { Aviso } from "@/shared/ui/Aviso/Aviso";
import { Boton } from "@/shared/ui/Boton/Boton";
import { Cargando } from "@/shared/ui/Estados/Cargando";
import { Modal } from "@/shared/ui/Modal/Modal";
import { pesos } from "@/shared/utilidades/formato";

import {
  useAbrirBorrador,
  useDescartarBorrador,
  useGenerarCotizacion,
  useGuardarBorrador,
  useGuardarHerramientasCotizacion,
  useHerramientasCotizacion,
  useModificarCotizacion,
  usePrepararModificacion,
  useRenglonesGuardados,
} from "./useBorrador";
import { HerramientasDeLaCotizacion } from "./HerramientasDeLaCotizacion";
import {
  partirDescripcion,
  precioConIvaEnLista,
  useCatalogo,
  useListaDePrecios,
  useDescargarCatalogo,
  useEsperarCotizacion,
  useProductoManoDeObra,
  useStock,
  useTarifaManoObra,
  type LineaCotizacion,
  type ProductoCatalogo,
} from "./useCotizador";
import {
  guardarBorradorLocal,
  leerBorradorLocal,
  olvidarBorradorLocal,
  marcarBorradorGuardado,
} from "@/lib/campo/borradorLocal";
import {
  BuscadorCliente,
  type ClienteElegido,
} from "@/modules/comercial/BuscadorCliente";
import { useColaDeFotos, sincronizarFotos } from "@/modules/campo/useColaDeFotos";
import { FotosOrden } from "@/modules/ordenes-servicio/FotosOrden";
import { useFotosDeLaOrden } from "@/modules/ordenes-servicio/useFotosOrden";
import { OfrecerEnvioAlCliente } from "@/modules/cotizaciones/OfrecerEnvioAlCliente";

import { CampoDeCantidad } from "./CampoDeCantidad";
import { unidadDeEntrada } from "./unidadDeEntrada";
import { CampoDePesos } from "./CampoDePesos";
import { MargenEnVivo } from "./MargenEnVivo";
import { PartirDeAnterior } from "./PartirDeAnterior";
import { useConceptosIvaLibre } from "./useConceptosIvaLibre";
import {
  useVendedores,
  useFijarVendedor,
  useVendedorDeLaCotizacion,
} from "./useVendedores";
import estilos from "./Cotizador.module.css";

type PropsCotizador = {
  abierto: boolean;
  ordenServicioId: string;
  ordenNumero: string;
  clienteNombre: string;
  /**
   * Adicional: lo que apareció a media obra. No reemplaza a la cotización que
   * la cuadrilla está ejecutando, se suma; y no abre una segunda orden de
   * trabajo porque es la misma obra.
   */
  adicional?: boolean;
  onCerrar: () => void;
  /**
   * Cotización a modificar. Si viene, el cotizador trabaja sobre un documento
   * que ya existe en World Office y conserva su número; si no, arma uno nuevo.
   */
  modificar?: { cotizacionId: string; numero: string | null } | null;
  /**
   * Cotización de venta de materiales, ya abierta. No nace de una orden, así
   * que no lleva mano de obra obligatoria.
   */
  libre?: { cotizacionId: string } | null;
  /** Qué hay que hacer. Se muestra mientras se cotiza, para no tener que recordarlo. */
  descripcionTrabajo?: string | null;
  /**
   * Se está cotizando en la obra, desde el teléfono del técnico.
   *
   * Cambia lo que se le cuenta al terminar, no lo que se hace. La oficina
   * necesita saber por dónde va el documento —que no ha viajado a World
   * Office, que lo siguiente es gerencia—; al técnico eso no le sirve de nada:
   * él ya no tiene nada que hacer con esa cotización, y contárselo lo deja
   * esperando un paso suyo que no existe.
   */
  enLaCalle?: boolean;
};

/**
 * Armar una cotización.
 *
 * Se trabaja sobre un borrador que vive en la matriz: se guarda, se cierra la
 * pantalla y se retoma después. World Office no se entera hasta que alguien
 * toca «Generar», que es cuando debe enterarse.
 */
/** La base sin IVA con 6 decimales, como la guarda World Office (0282). */
const seisDecimales = (n: number) => Math.round(n * 1e6) / 1e6;

/** Las únicas tasas de IVA que admite la DIAN (0267). */
const IVAS_DIAN = [0, 0.05, 0.19];
const ivaValido = (iva: number) =>
  IVAS_DIAN.some((v) => Math.abs(v - iva) < 1e-9);

export function Cotizador({
  abierto,
  ordenServicioId,
  ordenNumero,
  clienteNombre,
  adicional = false,
  onCerrar,
  modificar = null,
  libre = null,
  descripcionTrabajo = null,
  enLaCalle = false,
}: PropsCotizador) {
  const esModificacion = modificar !== null;
  const esLibre = libre !== null;
  const [cotizacionId, setCotizacionId] = useState<string | null>(null);
  const [termino, setTermino] = useState("");
  const [lineas, setLineas] = useState<LineaCotizacion[]>([]);
  // 0301: los únicos productos a los que se les cambia el IVA.
  const { data: ivaLibre = new Set<number>() } = useConceptosIvaLibre();
  const [confirmandoVaciar, setConfirmandoVaciar] = useState(false);
  const [sinGuardar, setSinGuardar] = useState(false);
  const [generado, setGenerado] = useState(false);
  /**
   * La cotización de una orden ya NO viaja a World Office al armarla: queda
   * cotizada aquí y viaja cuando gerencia la aprueba. `fn_generar_cotizacion`
   * lo dice devolviendo null en vez de un id de cola.
   *
   * Sin esto la pantalla se quedaba en «Generando en World Office» esperando
   * un número que ya nadie va a mandar.
   */
  const [quedaEnCasa, setQuedaEnCasa] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * Se está cotizando sin señal: la cotización todavía no existe en la base.
   * Lo escrito vive en el teléfono hasta que haya cobertura.
   */
  const [sinCotizacionEnLaBase, setSinCotizacionEnLaBase] = useState(false);

  const abrir = useAbrirBorrador();
  const guardar = useGuardarBorrador();
  const generar = useGenerarCotizacion();
  // 0302: la descripción del trabajo, que escribe quien cotiza. Es la de la
  // orden: el concepto que viaja a World Office cuando gerencia la aprueba.
  const [descripcion, setDescripcion] = useState(descripcionTrabajo ?? "");
  useEffect(() => setDescripcion(descripcionTrabajo ?? ""), [descripcionTrabajo]);
  // Las fotos de la visita: subidas o esperando señal en el teléfono.
  const { data: fotosSubidas, isLoading: cargandoFotos } = useFotosDeLaOrden(
    esLibre ? null : ordenServicioId
  );
  const { enCola: fotosEnCola } = useColaDeFotos(
    esLibre ? null : ordenServicioId
  );
  const faltaLaFoto =
    !esLibre &&
    !cargandoFotos &&
    (fotosSubidas?.length ?? 0) === 0 &&
    fotosEnCola.length === 0;
  const [sincronizandoFotos, setSincronizandoFotos] = useState(false);
  const esErrorDeFotos = Boolean(
    error && error.includes("no tiene fotos de la visita")
  );
  const necesitaFoto =
    !esLibre &&
    (faltaLaFoto ||
      esErrorDeFotos ||
      ((fotosSubidas?.length ?? 0) === 0 && fotosEnCola.length > 0));
  // El vendedor que viaja a World Office. Vacío = se deduce de quien cotiza.
  const { vendedores } = useVendedores();
  const fijarVendedor = useFijarVendedor();
  const [vendedor, setVendedor] = useState<number | null>(null);
  // Si la base no deja cambiarlo (p. ej. ya está en World Office), se dice y
  // el selector vuelve al que estaba. Antes se quedaba mostrando el nuevo.
  const [errorVendedor, setErrorVendedor] = useState<string | null>(null);
  // El que ya tiene puesto, para que el selector no arranque en «Automático»
  // haciendo creer que no hay ninguno.
  const vendedorGuardado = useVendedorDeLaCotizacion(cotizacionId);
  // Cliente nuevo al modificar una cotización SIN orden. Null = se deja el que
  // tiene. Con orden detrás no aplica: el cliente es el de la orden.
  const [clienteNuevo, setClienteNuevo] = useState<ClienteElegido | null>(null);
  const descartar = useDescartarBorrador();
  const preparar = usePrepararModificacion();
  const modificarCotizacion = useModificarCotizacion();

  const { guardados, cargando: cargandoRenglones, listos: renglonesListos } =
    useRenglonesGuardados(cotizacionId);

  /*
   * NO SE EDITA HASTA QUE LLEGUE LO GUARDADO (0325).
   *
   * Al abrir, la pantalla tarda en traer los renglones que ya tiene la
   * cotización —segundos, cuando la base va lenta—. Si en ese rato se
   * agregaba un producto o se tocaban las herramientas, la pantalla quedaba
   * «con cambios» sobre una lista vacía, y el autoguardado mandaba ESA lista
   * encima de lo guardado: OS-2026-0126 quedó con 0 de 5 productos, y la
   * OS-2026-0158 se generó con 2 de 8. Ahora, hasta que no llegan, ni se
   * agrega ni se guarda. Sin señal no hay nada que esperar: se cotiza en el
   * teléfono como siempre.
   */
  const listoParaEditar =
    sinCotizacionEnLaBase || (cotizacionId !== null && renglonesListos);
  const { herramientas: herramientasBd } = useHerramientasCotizacion(cotizacionId);
  const guardarHerramientas = useGuardarHerramientasCotizacion();
  const [herramientasRequeridas, setHerramientasRequeridas] = useState<
    Record<string, number>
  >({});

  useEffect(() => {
    if (herramientasBd && Object.keys(herramientasBd).length > 0 && !sinGuardar) {
      setHerramientasRequeridas(herramientasBd);
    }
  }, [herramientasBd, sinGuardar]);

  const { productos, buscando, sinSenal } = useCatalogo(termino);
  // El catálogo se mantiene descargado para poder cotizar en obra sin señal.
  const catalogoLocal = useDescargarCatalogo();
  const manoDeObra = useProductoManoDeObra();
  const tarifaManoObra = useTarifaManoObra();
  // La lista de precios del cliente en World Office (0285): la de la orden o,
  // en una venta libre, la del cliente escogido. Sin dato, la 1.
  const { data: listaDePrecios = 1 } = useListaDePrecios(
    esLibre ? null : ordenServicioId,
    esLibre ? clienteNuevo?.id_tercero ?? null : null
  );
  const { solicitud, agotado } = useEsperarCotizacion(
    generado && !quedaEnCasa ? cotizacionId : null
  );

  // Al abrir, se pide el borrador de esta orden. Si ya existía, se retoma.
  //
  // El objeto de la mutación cambia en cada render, así que no puede ir en las
  // dependencias: se guarda en una referencia. Y el candado evita pedir dos
  // borradores mientras la primera petición está en vuelo.
  const pidiendo = useRef(false);
  const abrirRef = useRef(abrir);
  abrirRef.current = abrir;

  const prepararRef = useRef(preparar);
  prepararRef.current = preparar;

  useEffect(() => {
    if (!abierto || cotizacionId || pidiendo.current) return;

    pidiendo.current = true;
    setError(null);

    // Modificar parte de lo que la cotización tiene hoy; la libre ya viene
    // abierta con su cliente; crear desde una orden parte de cero.
    const trabajo = modificar
      ? prepararRef.current
          .mutateAsync(modificar.cotizacionId)
          .then(() => modificar.cotizacionId)
      : libre
      ? Promise.resolve(libre.cotizacionId)
      : abrirRef.current.mutateAsync({ ordenServicioId, adicional });

    trabajo
      .then(setCotizacionId)
      .catch((fallo: unknown) => {
        /*
         * SIN SEÑAL SE COTIZA IGUAL, AUNQUE LA BASE NO PUEDA ABRIR NADA.
         *
         * `fn_abrir_borrador` necesita servidor. Sin él, la pantalla se
         * quedaba en «Abriendo el borrador…» para siempre y el técnico no
         * podía ni empezar a cotizar en la obra, que es donde más falta
         * hace. Ahora se trabaja sin identificador: los renglones se
         * guardan en el teléfono y la cotización se crea de verdad cuando
         * haya cobertura.
         */
        if (!navigator.onLine) {
          setSinCotizacionEnLaBase(true);
          return;
        }

        setError(
          fallo instanceof Error
            ? fallo.message
            : "No fue posible abrir la cotización."
        );
      })
      .finally(() => {
        pidiendo.current = false;
      });
    // `adicional` entra en las dependencias: decide QUÉ borrador se abre. Sin
    // él, abrir el cotizador en modo adicional después de haberlo abierto
    // normal reutilizaría el efecto viejo y armaría la principal.
  }, [abierto, cotizacionId, ordenServicioId, modificar, libre, adicional]);

  // Lo guardado manda mientras no haya cambios sin guardar: así al reabrir se
  // ve lo que quedó grabado y no un residuo del estado anterior.
  /*
   * LO QUE QUEDÓ EN EL TELÉFONO MANDA SI NO SE PUDO GUARDAR.
   *
   * Al reabrir, si hay un borrador local con renglones y el servidor no tiene
   * ninguno —porque aquel día no hubo señal—, se recupera lo del teléfono. Si
   * el servidor sí tiene, manda el servidor: es lo que vieron los demás.
   */
  const recuperado = useRef(false);

  useEffect(() => {
    if (esModificacion || esLibre || recuperado.current) return;
    if (cargandoRenglones || sinGuardar) return;

    const local = leerBorradorLocal(ordenServicioId);
    if (!local) return;
    if (local.herramientas && Object.keys(local.herramientas).length > 0) {
      setHerramientasRequeridas(local.herramientas);
    }
    if (local.lineas.length === 0) return;
    if (guardados.length > 0) return;

    recuperado.current = true;
    setLineas(local.lineas as LineaCotizacion[]);
    setSinGuardar(true);
  }, [
    guardados,
    cargandoRenglones,
    sinGuardar,
    ordenServicioId,
    esModificacion,
    esLibre,
  ]);

  useEffect(() => {
    if (cargandoRenglones || sinGuardar) return;

    setLineas(
      guardados.map((r) => ({
        id_inventario: r.id_inventario,
        descripcion: r.descripcion,
        // La mano de obra por horas se guarda como «1 Global × total» (0241);
        // aquí se vuelve a ver en horas, que es como se cotizó.
        ...(r.es_mano_de_obra && r.horas_mano_obra
          ? {
              cantidad: Number(r.horas_mano_obra),
              precio_unitario:
                Number(r.precio_unitario) / Number(r.horas_mano_obra),
            }
          : {
              cantidad: Number(r.cantidad),
              precio_unitario: Number(r.precio_unitario),
            }),
        iva: Number(r.iva),
        es_mano_de_obra: r.es_mano_de_obra,
        // Sin esto la unidad se perdía al reabrir: la columna se veía vacía y
        // al guardar de nuevo se grababa null encima de los metros (0189).
        // Y el cable que quedó en «Und.» se abre en metros (unidadDeEntrada).
        unidad: r.es_mano_de_obra
          ? r.horas_mano_obra
            ? "Hora"
            : r.unidad ?? null
          : unidadDeEntrada(r.descripcion, r.unidad ?? null),
      }))
    );
  }, [guardados, cargandoRenglones, sinGuardar]);

  /*
   * EL BORRADOR SE GUARDA SOLO.
   *
   * Hasta ahora lo que se iba armando vivía únicamente en la memoria del
   * navegador: se grababa cuando alguien pulsaba «Guardar borrador», y nada
   * más. Cualquier interrupción —el teléfono que descarta la pestaña al salir
   * un momento, la aplicación que se reinicia, un toque en atrás— borraba el
   * trabajo entero sin avisar, y al volver con «Seguir cotizando» aparecía la
   * cotización vacía. Pasó de verdad, con una cotización ya bien avanzada.
   *
   * Pedirle a alguien que se acuerde de guardar mientras cotiza en la calle es
   * ponerle una trampa: la que más renglones lleva es la que más duele perder
   * y la que más tarda en escribirse.
   *
   * Se guarda un segundo y medio después del último cambio —no en cada tecla,
   * que serían diez llamadas por renglón— y el botón queda como está: si algo
   * falla, sigue ahí para intentarlo a mano.
   *
   * No aplica al MODIFICAR: ahí no hay borrador que guardar, el documento ya
   * existe en World Office y lo que se manda lo reemplaza de una vez.
   */
  /*
   * Y ANTES QUE AL SERVIDOR, AL TELÉFONO.
   *
   * El autoguardado de abajo necesita red y una cotización ya abierta en la
   * base. En la obra puede no haber ninguna de las dos: sin señal
   * `fn_abrir_borrador` no responde, así que no hay `cotizacionId`, el
   * guardado nunca se intentaba y media hora de cotización se perdía al
   * cerrar la pantalla. Johan: «no puede perder».
   *
   * Esto escribe en el almacenamiento del propio teléfono en cada cambio,
   * pase lo que pase. Es la red de seguridad; lo de abajo sigue siendo lo
   * que de verdad guarda.
   */
  useEffect(() => {
    if (esModificacion || generado) return;
    // Vacío solo se escribe si antes había algo: es que borró los renglones,
    // y la copia vieja no puede quedar diciendo lo contrario.
    if (lineas.length === 0 && !leerBorradorLocal(ordenServicioId)) return;
    // Lo que se acaba de cargar del servidor no es un cambio: no hay nada
    // pendiente que subir.
    if (!sinGuardar) return;

    guardarBorradorLocal({
      ordenServicioId,
      adicional,
      lineas,
      cotizacionId,
      herramientas: herramientasRequeridas,
    });
  }, [
    lineas,
    ordenServicioId,
    adicional,
    cotizacionId,
    esModificacion,
    generado,
    sinGuardar,
    herramientasRequeridas,
  ]);

  useEffect(() => {
    if (esModificacion || generado) return;
    // Con cero renglones también: borrarlos todos es un cambio, y antes no
    // se guardaba —al reabrir volvían a aparecer—.
    if (!cotizacionId || !sinGuardar) return;
    if (!listoParaEditar) return;

    const reloj = setTimeout(() => {
      void guardarBorrador();
    }, 1500);

    return () => clearTimeout(reloj);
    // `guardarBorrador` se rehace en cada render y meterla aquí dispararía el
    // efecto sin parar; lo que de verdad manda es qué renglones hay.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineas, sinGuardar, cotizacionId, esModificacion, generado, listoParaEditar]);

  const totales = useMemo(() => {
    const base = lineas.reduce((s, l) => s + l.cantidad * l.precio_unitario, 0);
    const iva = lineas.reduce(
      (s, l) => s + l.cantidad * l.precio_unitario * l.iva,
      0
    );
    const obra = lineas
      .filter((l) => l.es_mano_de_obra)
      .reduce((s, l) => s + l.cantidad * l.precio_unitario, 0);

    // El margen se mide sobre la base y no sobre el total: el IVA se recauda
    // y se entrega, no es ingreso.
    const porcentajeObra = base > 0 ? (obra / base) * 100 : 0;

    return {
      base,
      iva,
      total: base + iva,
      obra,
      materiales: base - obra,
      porcentajeObra,
    };
  }, [lineas]);

  // El adicional no la exige (0283): la mano de obra ya se cotizó en la
  // principal. Johan: «ya se cotizó en la primera»; en la FEV 3076 le
  // pusieron $1 para poder generarla.
  const exigeManoDeObra = !esLibre && !adicional;
  const tieneManoDeObra = lineas.some(
    (l) => l.es_mano_de_obra && l.precio_unitario > 0
  );

  /*
   * EL STOCK NO ES ASUNTO DEL TÉCNICO.
   *
   * Johan: «las alertas de material disponible y material por comprar no les
   * interesa a los técnicos». Y es verdad: el que está en la obra cotiza lo
   * que el trabajo necesita, no lo que casualmente hay en bodega hoy. Decirle
   * «no hay en bodega» mientras le pone precio a una instalación solo le
   * invita a cambiar el material por otro peor, o a dudar de un número que
   * está bien puesto. Quien compra es la oficina, y lo que falte se va solo a
   * una orden de compra cuando se apruebe.
   *
   * En la oficina el aviso SÍ sirve —ahí sí se decide si se cambia o se
   * compra—, así que se queda. `enLaCalle` es el mismo interruptor que ya
   * distingue las dos pantallas.
   *
   * Y no es solo ocultarlo: en la calle ni siquiera se pide. Son consultas de
   * existencias que no se van a enseñar, y allí la señal es la que es.
   */
  const stockPorId = useStock(
    useMemo(
      () => (enLaCalle ? [] : lineas.map((l) => l.id_inventario)),
      [lineas, enLaCalle]
    )
  );
  // Renglones con un IVA que la DIAN no acepta (productos de WO mal digitados).
  const ivasMalos = lineas.filter((l) => !ivaValido(l.iva)).length;

  const faltantes = useMemo(
    () =>
      enLaCalle
        ? 0
        : lineas.filter((l) => {
            if (l.es_mano_de_obra) return false;
            const stock = stockPorId.get(l.id_inventario);
            return stock != null && stock < l.cantidad;
          }).length,
    [lineas, stockPorId, enLaCalle]
  );

  function agregar(producto: ProductoCatalogo) {
    if (!listoParaEditar) return;
    setTermino("");
    setSinGuardar(true);

    setLineas((previas) => {
      const yaEsta = previas.find(
        (l) => l.id_inventario === producto.id_inventario
      );
      if (yaEsta) {
        return previas.map((l) =>
          l.id_inventario === producto.id_inventario
            ? { ...l, cantidad: l.cantidad + 1 }
            : l
        );
      }

      return [
        ...previas,
        {
          id_inventario: producto.id_inventario,
          descripcion: partirDescripcion(producto.descripcion).nombre,
          cantidad: 1,
          // La del catálogo como punto de partida —en metros si es cable—;
          // abajo se puede cambiar.
          unidad: producto.es_mano_de_obra
            ? producto.unidad ?? null
            : unidadDeEntrada(producto.descripcion, producto.unidad ?? null),
          // La mano de obra se cobra por HORAS: su precio unitario es la tarifa
          // por hora de la empresa y la cantidad son las horas del trabajo, así
          // el valor = horas × tarifa. Si aún no hay tarifa configurada, cae al
          // precio del catálogo como antes. Los materiales sí llegan del
          // catálogo SIN redondear (0282): el medidor de $230.000 con IVA
          // tiene base $193.277,310924; redondeada a $193.277 la COT daba
          // $229.999,63 por unidad y la factura «no daba» (OS-2026-0113).
          // Seis decimales, como los guarda World Office.
          // Y con la lista del cliente (0285): Hernán Darío está en la 3.
          precio_unitario:
            producto.es_mano_de_obra && tarifaManoObra > 0
              ? tarifaManoObra
              : listaDePrecios === 1
              ? seisDecimales(producto.precio_sin_iva ?? 0)
              : seisDecimales(
                  (precioConIvaEnLista(producto, listaDePrecios) ?? 0) /
                    (1 + (producto.iva ?? 0.19))
                ),
          iva: producto.iva ?? 0.19,
          es_mano_de_obra: producto.es_mano_de_obra,
        },
      ];
    });
  }

  /*
   * LA MANO DE OBRA, SIEMPRE A LA TARIFA.
   *
   * Johan: «se escriben horas, y esas horas se calculan por un valor
   * interno… el cálculo lo hace la app». La base lo impone al guardar (0225);
   * aquí se refleja para que lo que se ve sea lo que va a quedar, también en
   * un borrador viejo que traía 600.000 como «1 unidad». La tarifa viene
   * guardada en el teléfono, así que esto funciona igual sin señal.
   *
   * La condición evita el bucle: solo se toca si hay algo que corregir.
   */
  useEffect(() => {
    if (tarifaManoObra <= 0) return;
    // La que va por VALOR TOTAL («Global», 0241) no se toca: ahí el precio
    // lo escribe quien cotiza.
    const porHoras = (l: LineaCotizacion) =>
      l.es_mano_de_obra && l.unidad !== "Global";
    setLineas((ls) =>
      ls.some(
        (l) =>
          porHoras(l) &&
          (l.precio_unitario !== tarifaManoObra || l.unidad !== "Hora")
      )
        ? ls.map((l) =>
            porHoras(l)
              ? { ...l, precio_unitario: tarifaManoObra, unidad: "Hora" }
              : l
          )
        : ls
    );
  }, [tarifaManoObra, lineas]);

  /*
   * MANO DE OBRA: EN HORAS O EN TOTAL (0241).
   *
   * Johan: «que se pueda cotizar en hora o el valor total… igual al cliente
   * siempre debe salirle el total, ninguna multiplicación». Al pasar de una
   * a otra se conserva la plata: 3 h a $45.000 pasan a un total de
   * $135.000, y un total de $200.000 vuelve como las horas que alcanza.
   */
  function modoDeManoDeObra(id: number, modo: "horas" | "total") {
    setLineas((ls) =>
      ls.map((l) => {
        if (l.id_inventario !== id || !l.es_mano_de_obra) return l;
        const total = l.cantidad * l.precio_unitario;
        if (modo === "total") {
          return {
            ...l,
            cantidad: 1,
            precio_unitario: Math.round(total),
            unidad: "Global",
          };
        }
        const tarifa = tarifaManoObra > 0 ? tarifaManoObra : l.precio_unitario;
        const horas =
          tarifa > 0 ? Math.max(0.5, Math.round((total / tarifa) * 2) / 2) : 1;
        return {
          ...l,
          cantidad: horas,
          precio_unitario: tarifa,
          unidad: "Hora",
        };
      })
    );
    setSinGuardar(true);
  }

  /**
   * PRECIOS ANTES O DESPUÉS DE IVA (oct 2026).
   *
   * Johan: «se cobraron 600.000 cerrados y hay que ponerse a adivinar el
   * número para que dé los 600». Con «Con IVA incluido» se escribe el precio
   * que paga el cliente y la base se calcula sola: precio / (1 + IVA). Lo
   * que se guarda sigue siendo el precio ANTES de IVA, que es lo que lleva
   * World Office; esto es solo cómo se escribe.
   */
  const [conIva, setConIva] = useState(false);
  const conIvaDe = (precio: number, iva: number) =>
    Math.round(precio * (1 + iva));
  // Seis decimales y no dos (0282): con dos, $600.000 con IVA dan
  // $504.201,68 y con cantidades grandes el total se corre un peso.
  const baseDe = (conElIva: number, iva: number) =>
    seisDecimales(conElIva / (1 + iva));

  function cambiar(
    id: number,
    campo: "cantidad" | "precio_unitario" | "iva",
    valor: number
  ) {
    setSinGuardar(true);
    setLineas((previas) =>
      previas.map((l) => {
        if (l.id_inventario !== id) return l;
        // Con IVA incluido, lo que se acordó es el precio CERRADO: si se
        // cambia el IVA, ese precio se queda y la base se recalcula (600.000
        // con 19 % o con 0 % siguen siendo 600.000). La mano de obra por
        // horas no: su tarifa la pone el sistema (0225).
        if (
          campo === "iva" &&
          conIva &&
          !(l.es_mano_de_obra && l.unidad !== "Global")
        ) {
          return {
            ...l,
            iva: valor,
            precio_unitario: baseDe(conIvaDe(l.precio_unitario, l.iva), valor),
          };
        }
        return { ...l, [campo]: valor };
      })
    );
  }

  /**
   * La unidad se cambia POR RENGLÓN, y no es un capricho de la pantalla.
   *
   * En World Office el maestro del producto casi siempre dice «Und.» —de 2.680
   * productos solo 7 dicen «m»— y son los asesores los que la ajustan en el
   * renglón al cotizar. El mismo cable sale unas veces en unidades y otras en
   * metros. Si aquí no se pudiera cambiar, una cotización de 35 metros de cable
   * saldría impresa como 35 unidades.
   */
  function cambiarUnidad(id: number, unidad: string) {
    setSinGuardar(true);
    setLineas((previas) =>
      previas.map((l) =>
        l.id_inventario === id ? { ...l, unidad: unidad.trim() || null } : l
      )
    );
  }

  function quitar(id: number) {
    setSinGuardar(true);
    setLineas((previas) => previas.filter((l) => l.id_inventario !== id));
  }

  /**
   * Empezar de cero.
   *
   * TIENE QUE BORRAR EN LA BASE, no solo en la pantalla. La primera versión
   * hacía setLineas([]) y ya, y parecía funcionar: el buscador de anteriores
   * volvía. Pero fn_traer_renglones_de INSERTA en la base, así que los
   * renglones seguían ahí — y al escoger otra cotización se sumaba a la
   * anterior. Vaciar sin vaciar es peor que no tener el botón.
   *
   * Se guarda un borrador vacío: fn_guardar_borrador borra y reinserta, así
   * que con la lista vacía deja la cotización limpia de verdad.
   */
  async function vaciar() {
    if (!cotizacionId) return;

    setError(null);

    try {
      await guardar.mutateAsync({ cotizacionId, renglones: [], vaciar: true });
      setLineas([]);
      // Sin cambios pendientes: lo que manda ahora es lo guardado, que está
      // vacío. Dejarlo en true haría que la pantalla no se sincronizara.
      setSinGuardar(false);
      setConfirmandoVaciar(false);
    } catch (fallo) {
      setError(
        fallo instanceof Error
          ? fallo.message
          : "No se pudo vaciar el borrador."
      );
    }
  }

  async function guardarBorrador(): Promise<boolean> {
    if (!cotizacionId) return false;
    setError(null);

    try {
      await guardar.mutateAsync({ cotizacionId, renglones: lineas });
      if (!esLibre && !esModificacion) {
        await guardarHerramientas.mutateAsync({
          cotizacionId,
          herramientas: herramientasRequeridas,
        });
      }
      setSinGuardar(false);
      marcarBorradorGuardado(ordenServicioId);
      return true;
    } catch (fallo) {
      setError(
        fallo instanceof Error ? fallo.message : "No fue posible guardar."
      );
      return false;
    }
  }

  /** Guardar y mandar son un solo gesto: nadie quiere generar algo distinto de lo que ve. */
  async function generarCotizacion() {
    if (!cotizacionId) return;
    setError(null);

    try {
      if (esModificacion) {
        // Modificar no pasa por el borrador: manda los renglones y World
        // Office reemplaza los del documento, conservando el número.
        const cola = await modificarCotizacion.mutateAsync({
          cotizacionId,
          // Modificar no pasa por fn_guardar_borrador, que es la que deja la
          // mano de obra como un total: se deja así aquí.
          renglones: lineas.map((l) =>
            l.es_mano_de_obra && l.unidad !== "Global"
              ? {
                  ...l,
                  cantidad: 1,
                  precio_unitario: Math.round(l.cantidad * l.precio_unitario),
                  unidad: "Global",
                  horas_mano_obra: l.cantidad,
                }
              : l
          ),
          motivo: clienteNuevo
            ? `Se ajustaron los renglones y se cambió el cliente a ${clienteNuevo.nombre}.`
            : "Se ajustaron los renglones desde la matriz.",
          // Solo sin orden: la base ignora este parámetro cuando hay orden.
          idTerceroNuevo: esLibre ? clienteNuevo?.id_tercero ?? null : null,
        });
        setSinGuardar(false);
        // Igual que al generar: sin solicitud en la cola no hay nada que
        // esperar de World Office. Sin esto la pantalla se quedaba dos
        // minutos en «Generando…» y terminaba diciendo que no hubo
        // respuesta, aunque los renglones ya estaban guardados.
        setQuedaEnCasa(cola === null);
      } else {
        if (!(await guardarBorrador())) return;
        const cola = await generar.mutateAsync({
          cotizacionId,
          descripcion: esLibre ? null : descripcion,
        });
        setQuedaEnCasa(cola === null);
      }

      setGenerado(true);
      // Ya está en la base: la copia del teléfono sobra y, si se quedara,
      // reaparecería como borrador la próxima vez.
      olvidarBorradorLocal(ordenServicioId);
    } catch (fallo) {
      setError(
        fallo instanceof Error
          ? fallo.message
          : "No fue posible mandar la cotización."
      );
    }
  }

  function cerrarTodo() {
    setCotizacionId(null);
    setLineas([]);
    setTermino("");
    setSinGuardar(false);
    setGenerado(false);
    setError(null);
    setClienteNuevo(null);
    onCerrar();
  }

  // ── Ya se generó: esperando el número ─────────────────────────────────
  if (generado) {
    const listo = quedaEnCasa || solicitud?.estado === "done";
    const fallo = solicitud?.estado === "error";

    return (
      <Modal
        abierto={abierto}
        titulo={
          quedaEnCasa
            ? esModificacion
              ? "Cambios guardados"
              : "Cotización lista"
            : esModificacion
            ? listo
              ? "Cotización actualizada"
              : "Guardando en World Office"
            : listo
            ? "Cotización creada"
            : "Generando en World Office"
        }
        onCerrar={cerrarTodo}
        pie={
          <Boton variante="principal" onClick={cerrarTodo}>
            {listo ? "Listo" : "Cerrar"}
          </Boton>
        }
      >
        <div className={estilos.espera}>
          {quedaEnCasa ? (
            enLaCalle ? (
              <p className={estilos.esperaTexto}>
                Listo. El precio quedó guardado en la orden {ordenNumero} y la
                oficina sigue desde aquí. Usted no tiene que hacer nada más con
                esta cotización.
              </p>
            ) : (
              <>
                <p className={estilos.esperaTexto}>
                  {esModificacion
                    ? `Los renglones quedaron actualizados en la orden ${ordenNumero}. Sigue esperando la aprobación de Gerencia.`
                    : `Quedó lista en la orden ${ordenNumero} y ahora espera la aprobación de Gerencia.`}
                </p>
                <p className={estilos.esperaTexto}>
                  No viaja a World Office todavía: allá llega cuando gerencia la
                  apruebe, y en ese mismo momento le sale al cliente para que la
                  acepte.
                </p>
                {/* Quien puede aprobar la manda desde aquí, si quiere. */}
                {!esModificacion && cotizacionId && (
                  <OfrecerEnvioAlCliente cotizacionId={cotizacionId} />
                )}
              </>
            )
          ) : listo ? (
            <>
              <p className={estilos.exito}>
                {solicitud?.numero_devuelto ?? modificar?.numero}
              </p>

              {/* CADA CASO DICE LO SUYO. Este texto estaba escrito solo para
                  el alta de una cotización de orden, y salía igual al
                  modificar —«creada», «ya existe»— y en una venta de
                  materiales, que no está amarrada a orden ninguna. */}
              <p className={estilos.esperaTexto}>
                {esModificacion
                  ? `Quedó actualizada en World Office por ${pesos(
                      solicitud?.wo_total ?? null
                    )}. El número no cambia.`
                  : esLibre
                  ? `Quedó creada en World Office por ${pesos(
                      solicitud?.wo_total ?? null
                    )} como venta de materiales.`
                  : `Ya existe en World Office por ${pesos(
                      solicitud?.wo_total ?? null
                    )} y quedó amarrada a la orden ${ordenNumero}.`}
              </p>

              {/* Una venta de materiales no pasa por gerencia: no hay trabajo
                  que aprobar ni orden que programar. */}
              {!esLibre && (
                <p className={estilos.esperaTexto}>
                  Ahora espera la aprobación de Gerencia.
                </p>
              )}
              {!esLibre && !esModificacion && cotizacionId && (
                <OfrecerEnvioAlCliente cotizacionId={cotizacionId} />
              )}

              <p className={estilos.reloj}>
                Tardó {solicitud?.segundos ?? 0} segundos
              </p>
            </>
          ) : fallo ? (
            <Aviso
              tipo="error"
              titulo={
                esModificacion
                  ? "World Office no aceptó el cambio"
                  : "World Office rechazó la cotización"
              }
            >
              {solicitud?.error_msg ?? "Sin detalle."}
            </Aviso>
          ) : agotado ? (
            <Aviso tipo="alerta" titulo="Sin respuesta todavía">
              La cotización quedó en la cola pero World Office no ha respondido
              en dos minutos. Puede cerrar: cuando responda, el número aparecerá
              solo en la orden.
            </Aviso>
          ) : (
            <>
              <span className={estilos.girador} aria-hidden="true" />
              <p className={estilos.esperaTitulo}>
                {esModificacion
                  ? "Guardando los cambios"
                  : "Creando la cotización"}
              </p>
              <p className={estilos.esperaTexto}>
                World Office recoge la cola cada veinte segundos y devuelve el
                número.
              </p>
              <p className={estilos.reloj}>{solicitud?.segundos ?? 0} s</p>
            </>
          )}
        </div>
      </Modal>
    );
  }

  // ── Armando el borrador ───────────────────────────────────────────────
  return (
    <Modal
      abierto={abierto}
      titulo={
        esModificacion
          ? `Modificar ${modificar?.numero ?? "cotización"}`
          : "Cotizar"
      }
      subtitulo={
        esModificacion
          ? // Neutral a propósito: mientras gerencia no la apruebe la cotización
            // no está en World Office, y decir que se reemplaza allá era
            // prometer algo que no pasa (0189).
            "Se reemplazan los renglones. El número no cambia."
          : "Se guarda como borrador. World Office no se entera hasta que la genere."
      }
      ancho
      onCerrar={cerrarTodo}
      pie={
        <>
          {/* Todos los botones del pie van en un solo grupo: sueltos, cada uno
              envolvía por su lado y el pie quedaba escalonado. En el teléfono
              «Generar» toma la fila entera —es lo que se pulsa— y los demás
              comparten la de arriba; en el escritorio van en una fila a la
              derecha. */}
          <div className={estilos.pieBotones}>
            {!esModificacion && lineas.length === 0 && cotizacionId && (
              <Boton
                variante="peligro"
                onClick={async () => {
                  // 0323: la base no deja descartar el borrador que otro está
                  // armando, y lo dice. Sin el catch, el botón no hacía nada.
                  try {
                    await descartar.mutateAsync(cotizacionId);
                    cerrarTodo();
                  } catch (fallo) {
                    setError(
                      fallo instanceof Error
                        ? fallo.message
                        : "No se pudo descartar el borrador."
                    );
                  }
                }}
                trabajando={descartar.isPending}
              >
                Descartar
              </Boton>
            )}

            <Boton onClick={cerrarTodo}>Cerrar</Boton>

            {/* Modificar no guarda borradores: la cotización ya existe en World
                Office y lo que se manda la reemplaza de una vez. */}
            {!esModificacion && (
              <Boton
                onClick={() => void guardarBorrador()}
                trabajando={guardar.isPending}
                // Guardar contra el servidor tampoco se puede sin señal; lo
                // escrito ya está en el teléfono.
                disabled={
                  !sinGuardar || lineas.length === 0 || sinCotizacionEnLaBase
                }
              >
                {sinGuardar ? "Guardar borrador" : "Guardado"}
              </Boton>
            )}

            <Boton
              variante="principal"
              onClick={() => void generarCotizacion()}
              trabajando={generar.isPending || modificarCotizacion.isPending}
              // La mano de obra solo se exige cuando la cotización nace de una
              // orden de servicio: una venta de materiales no es un trabajo.
              // Sin cotización en la base no hay nada que generar todavía:
              // el botón se apaga y el rótulo dice por qué.
              disabled={
                lineas.length === 0 ||
                (exigeManoDeObra && !tieneManoDeObra) ||
                sinCotizacionEnLaBase ||
                ivasMalos > 0
              }
            >
              {esModificacion
                ? "Guardar cambios"
                : sinCotizacionEnLaBase
                ? "Se generará con señal"
                : "Generar cotización"}
            </Boton>
          </div>
        </>
      }
    >
      <div className={estilos.contexto}>
        <div className={estilos.contextoEncima}>
          <span className={estilos.contextoOrden}>{ordenNumero}</span>
          <span className={estilos.contextoCliente}>{clienteNombre}</span>
          {sinGuardar && lineas.length > 0 && (
            <span className={estilos.sinGuardar}>Cambios sin guardar</span>
          )}
        </div>

        {/* Qué hay que hacer, a la vista mientras se cotiza: es lo que dice
            qué materiales lleva, y tenerlo que recordar es como se olvidan.
            0302: y se escribe aquí («la observación del trabajo a ejecutar»):
            es la descripción de la orden, la que viaja a World Office como
            concepto cuando gerencia la aprueba. Gerencia la puede editar en
            la orden. */}
        {!esLibre && !esModificacion ? (
          <label className={estilos.descripcionCampo}>
            <span>Descripción del trabajo a ejecutar</span>
            <textarea
              rows={3}
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              placeholder="Qué hay que hacer en la obra: va en la cotización y en la factura."
              maxLength={2000}
            />
          </label>
        ) : (
          descripcionTrabajo?.trim() && (
            <p className={estilos.descripcionTrabajo}>
              {descripcionTrabajo.trim()}
            </p>
          )
        )}

        {/* EL VENDEDOR, DENTRO DE LA FICHA Y NO SUELTO ENCIMA DEL BUSCADOR.
            Flotando entre dos bloques no se leía como un dato de esta
            cotización —parecía un filtro de la pantalla—. Aquí está junto a
            la orden y el cliente, que es con lo que forma sentido: de quién
            es el trabajo y quién lo vende. */}
        <div className={estilos.vendedorFila}>
          <span className={estilos.vendedorRotulo}>Vendedor</span>
          <select
            className={estilos.vendedorSelect}
            value={vendedor ?? vendedorGuardado ?? ""}
            disabled={!cotizacionId}
            onChange={(e) => {
              const elegido = e.target.value ? Number(e.target.value) : null;
              const antes = vendedor;
              setVendedor(elegido);
              setErrorVendedor(null);
              if (cotizacionId) {
                fijarVendedor
                  .mutateAsync({ cotizacionId, idVendedor: elegido })
                  .catch((fallo: unknown) => {
                    setVendedor(antes);
                    setErrorVendedor(
                      fallo instanceof Error
                        ? fallo.message
                        : "No se pudo cambiar el vendedor."
                    );
                  });
              }
            }}
          >
            <option value="">Automático (quien cotiza)</option>
            {vendedores.map((v) => (
              <option key={v.id_tercero} value={v.id_tercero}>
                {v.nombre}
              </option>
            ))}
          </select>
          {errorVendedor && (
            <span className={estilos.vendedorError} role="alert">
              {errorVendedor}
            </span>
          )}
          <span className={estilos.vendedorNota}>
            Es el que queda en World Office
          </span>
        </div>

        {/* 0318: Herramientas que el técnico estimó necesarias para el trabajo */}
        {!esLibre && !esModificacion && (
          <HerramientasDeLaCotizacion
            eleccion={herramientasRequeridas}
            onCambiar={(nuevas) => {
              // 0325: tocarlas antes de que cargue lo guardado autoguardaba
              // una lista vacía encima.
              if (!listoParaEditar) return;
              setHerramientasRequeridas(nuevas);
              setSinGuardar(true);
            }}
          />
        )}
      </div>

      {/* CAMBIAR EL CLIENTE, SOLO SIN ORDEN DETRÁS.
          En una cotización de una orden el cliente es el de la orden y no se
          toca aquí. En una venta de materiales el cliente vive en la propia
          cotización, así que se puede cambiar al modificarla. El actual queda a
          la vista; el buscador —el mismo del catálogo de World Office— es para
          cambiarlo, y es opcional: si no se elige otro, se conserva. */}
      {esModificacion && esLibre && (
        <div className={estilos.cambiarCliente}>
          <p className={estilos.clienteActual}>
            Cliente actual: <b>{clienteNuevo?.nombre ?? clienteNombre}</b>
          </p>
          <BuscadorCliente
            etiqueta="Cambiar cliente"
            elegido={clienteNuevo}
            onElegir={setClienteNuevo}
            ayuda="Opcional. Búsquelo en World Office; si no lo elige, se conserva el de arriba."
          />
        </div>
      )}

      {/* El «no tiene fotos» de la base sobra si la cámara ya está aquí
          abajo pidiéndola, o si ya se subió una: se esconde en esos dos
          casos y se ve en el resto (por ejemplo, foto aún sin subir). */}
      {error &&
        !(
          esErrorDeFotos &&
          (necesitaFoto || (fotosSubidas?.length ?? 0) > 0)
        ) && (
          <div style={{ marginBottom: "var(--e-4)" }}>
            <Aviso tipo="error">{error}</Aviso>
          </div>
        )}

      {/* LA FOTO SE TOMA AQUÍ MISMO.
          Sin una foto de la visita la base no deja generar (0204, lo pidió
          Johan). Si la orden no tiene foto en la base, o si el servidor
          acaba de rechazar por falta de foto, se muestra la cámara aquí
          mismo para que el técnico nunca quede atrapado con un error rojo
          sin acción posible. */}
      {necesitaFoto && (
        <div className={estilos.faltaFoto}>
          <p className={estilos.faltaFotoTitulo}>
            {esErrorDeFotos
              ? "Se requiere al menos una foto de la visita"
              : "Falta la foto de la visita"}
          </p>
          <p className={estilos.faltaFotoTexto}>
            Tome al menos una de cómo está la instalación. Sin ella no se puede
            generar la cotización.
          </p>

          {fotosEnCola.length > 0 && (fotosSubidas?.length ?? 0) === 0 && (
            <div className={estilos.colaPendienteAviso}>
              <p>
                Hay {fotosEnCola.length}{" "}
                {fotosEnCola.length === 1
                  ? "foto pendiente"
                  : "fotos pendientes"}{" "}
                por subir al servidor.
              </p>
              <Boton
                tamano="pequeno"
                onClick={async () => {
                  setSincronizandoFotos(true);
                  try {
                    await sincronizarFotos();
                  } finally {
                    setSincronizandoFotos(false);
                  }
                }}
                trabajando={sincronizandoFotos}
              >
                Subir fotos pendientes ahora
              </Boton>
            </div>
          )}

          <FotosOrden
            ordenServicioId={ordenServicioId}
            sello={{ referencia: ordenNumero }}
          />
        </div>
      )}

      {/* 0302: ya con una, la cámara seguía escondida y no se podían tomar
          más (la oficina: «cuando se le asigna orden para cotizar solo le
          deja una foto»). Ahora siguen a la mano, como en autogestión. */}
      {!esLibre && !necesitaFoto && !cargandoFotos && (
        <details className={estilos.masFotos}>
          <summary>
            Fotos de la visita · {(fotosSubidas?.length ?? 0) + fotosEnCola.length}
          </summary>
          <FotosOrden
            ordenServicioId={ordenServicioId}
            sello={{ referencia: ordenNumero }}
          />
        </details>
      )}

      {/* El vendedor viaja a World Office con la cotización. Va aquí y no en el
          pie del modal: quien cotiza lo decide al principio, no al generar. */}

      {!cotizacionId && !sinCotizacionEnLaBase ? (
        <Cargando mensaje="ABRIENDO BORRADOR COTIZACIÓN..." />
      ) : (
        <>
          <div className={estilos.buscadorZona}>
            <input
              className={estilos.buscador}
              type="search"
              placeholder={
                listoParaEditar
                  ? "Buscar material o servicio en World Office…"
                  : "Cargando lo que ya tiene guardado…"
              }
              value={termino}
              onChange={(e) => setTermino(e.target.value)}
              disabled={!listoParaEditar}
              aria-label="Buscar en el catálogo"
            />

            {/* SIN COTIZACIÓN EN LA BASE: lo escrito vive en el teléfono.
                Hay que decirlo con todas las letras —el técnico tiene que
                saber que su media hora de trabajo está a salvo y que la
                cotización saldrá cuando haya señal—. */}
            {sinCotizacionEnLaBase && (
              <p className={estilos.sinSenal}>
                Sin señal: lo que armes se guarda en este teléfono y no se
                pierde. La cotización se crea sola cuando vuelva la conexión;
                mientras tanto no se puede generar.
              </p>
            )}

            {/* SIN SEÑAL SE SIGUE COTIZANDO, pero diciéndolo: el stock que
                se ve es el del último día con internet y con eso no se
                decide una compra. */}
            {sinSenal && (
              <p className={estilos.sinSenal}>
                Sin señal: buscando en el catálogo guardado en este teléfono
                {catalogoLocal.productos > 0 &&
                  ` (${catalogoLocal.productos} productos)`}
                . Los precios sirven; el stock de bodega no se puede consultar
                hasta que vuelva la conexión.
              </p>
            )}

            {termino.trim().length >= 2 && (
              <div className={estilos.resultados}>
                {buscando && productos.length === 0 ? (
                  <p className={estilos.sinResultados}>Buscando…</p>
                ) : productos.length === 0 ? (
                  <p className={estilos.sinResultados}>
                    Ningún producto con ese nombre. Si no existe, hay que
                    crearlo en World Office.
                  </p>
                ) : (
                  productos.map((producto) => {
                    const { codigo, nombre } = partirDescripcion(
                      producto.descripcion
                    );

                    return (
                      <button
                        key={producto.id_inventario}
                        type="button"
                        className={estilos.resultado}
                        onClick={() => agregar(producto)}
                      >
                        <span className={estilos.resultadoNombre}>
                          {codigo && (
                            <span className={estilos.codigoProducto}>
                              {codigo}
                            </span>
                          )}
                          {nombre}
                          {producto.es_mano_de_obra && (
                            <span className={estilos.marcaMo}>Margen</span>
                          )}
                          {/* El stock, salvo en la mano de obra, que no tiene. */}
                          {/* SIN SEÑAL NO SE DICE NADA DEL STOCK.
                              El catálogo del teléfono no lo trae, así que
                              pintarlo en rojo como «Sin stock» sería afirmar
                              que la bodega está vacía cuando lo único cierto
                              es que no se puede preguntar. */}
                          {!producto.es_mano_de_obra &&
                            (sinSenal ? (
                              <span className={estilos.stockSinDato}>
                                Stock: sin señal
                              </span>
                            ) : producto.stock > 0 ? (
                              <span className={estilos.stockHay}>
                                Hay {producto.stock}
                              </span>
                            ) : (
                              <span className={estilos.stockNo}>Sin stock</span>
                            ))}
                        </span>

                        <span
                          className={`${estilos.resultadoPrecio} ${
                            producto.precio_a_convenir ? estilos.aConvenir : ""
                          }`}
                        >
                          {producto.precio_a_convenir
                            ? "Precio a convenir"
                            : pesos(
                                precioConIvaEnLista(producto, listaDePrecios)
                              )}
                        </span>
                      </button>
                    );
                  })
                )}
              </div>
            )}
          </div>

          {/* Solo con el borrador vacío y solo cuando nace de una orden: una
              modificación ya tiene sus renglones y una venta de materiales no
              se parece a un trabajo anterior. */}
          {/* Partir de una anterior necesita la cotización ya abierta en la
              base: sin señal no existe todavía, así que no se ofrece. */}
          {lineas.length === 0 &&
            !esModificacion &&
            !esLibre &&
            cotizacionId && (
              <PartirDeAnterior
                ordenServicioId={ordenServicioId}
                cotizacionId={cotizacionId}
                onTraido={() => setSinGuardar(false)}
              />
            )}

          {lineas.length === 0 ? (
            <p className={estilos.vacio}>
              Busque los materiales y la mano de obra que lleva este trabajo.
              <br />
              El precio que se muestra incluye IVA; a World Office viaja el de
              antes de impuestos.
            </p>
          ) : (
            <>
              <div className={estilos.barraRenglones}>
                <span className={estilos.cuantosRenglones}>
                  {lineas.length}{" "}
                  {lineas.length === 1 ? "renglón" : "renglones"}
                </span>

                {/* Con qué lista de World Office se cotiza (0285). */}
                {listaDePrecios !== 1 && (
                  <span
                    className={estilos.listaDePrecios}
                    title="World Office tiene tres listas de precio y este cliente tiene asignada esta. Al facturarle, WO propone estos precios."
                  >
                    Lista {listaDePrecios} del cliente
                  </span>
                )}

                <span
                  className={estilos.modoMo}
                  role="group"
                  aria-label="Cómo se escriben los precios"
                >
                  <button
                    type="button"
                    className={!conIva ? estilos.modoMoActivo : undefined}
                    aria-pressed={!conIva}
                    onClick={() => setConIva(false)}
                  >
                    Antes de IVA
                  </button>
                  <button
                    type="button"
                    className={conIva ? estilos.modoMoActivo : undefined}
                    aria-pressed={conIva}
                    onClick={() => setConIva(true)}
                    title="Escriba el precio que paga el cliente; la base se calcula sola."
                  >
                    Con IVA incluido
                  </button>
                </span>

                {/* Dos toques: borra renglones ya guardados y no hay
                    deshacer. Un modal para esto sería mucho; un clic solo,
                    poco. */}
                {confirmandoVaciar ? (
                  <span className={estilos.confirmarVaciar}>
                    <button
                      type="button"
                      className={estilos.vaciar}
                      onClick={() => setConfirmandoVaciar(false)}
                    >
                      No
                    </button>
                    <button
                      type="button"
                      className={estilos.vaciarSeguro}
                      disabled={guardar.isPending}
                      onClick={() => void vaciar()}
                    >
                      {guardar.isPending
                        ? "Vaciando…"
                        : "Sí, borrar los renglones"}
                    </button>
                  </span>
                ) : (
                  <button
                    type="button"
                    className={estilos.vaciar}
                    onClick={() => setConfirmandoVaciar(true)}
                  >
                    Vaciar y empezar de cero
                  </button>
                )}
              </div>

              {/* Faltantes de bodega: se avisa aquí para poder cambiar el
                  producto antes de generar. Los que queden se van solos a una
                  orden de compra cuando gerencia apruebe la cotización. */}
              {ivasMalos > 0 && (
                <div className={estilos.avisoFaltantes}>
                  {ivasMalos === 1
                    ? "Un producto trae de World Office un IVA que la DIAN no acepta."
                    : `${ivasMalos} productos traen de World Office un IVA que la DIAN no acepta.`}{" "}
                  Escoge 0, 5 o 19 % en el renglón marcado para poder generar la
                  cotización.
                </div>
              )}

              {faltantes > 0 && (
                <div className={estilos.avisoFaltantes}>
                  {faltantes === 1
                    ? "Hay 1 material sin stock suficiente en bodega."
                    : `Hay ${faltantes} materiales sin stock suficiente en bodega.`}{" "}
                  Puedes cambiarlos ahora; los que dejes así pasarán a una orden
                  de compra cuando se apruebe la cotización.
                </div>
              )}

              {/* Las dos que usa World Office hoy. Es sugerencia, no
                  encierro: el campo admite lo que haga falta.

                  FUERA DE LA TABLA a propósito: dentro, el navegador lo
                  expulsa al reacomodar el HTML —una <datalist> no es hija
                  válida de <table>— y las sugerencias de unidad dejaban de
                  aparecer. */}
              <datalist id="unidades-medida">
                <option value="Und." />
                <option value="m" />
              </datalist>

              <table className={estilos.tabla}>
                <thead>
                  <tr>
                    <th>Concepto</th>
                    <th className={estilos.derecha} style={{ width: 92 }}>
                      Cantidad
                    </th>
                    <th className={estilos.derecha} style={{ width: 84 }}>
                      Unidad
                    </th>
                    <th className={estilos.derecha} style={{ width: 134 }}>
                      {conIva ? "Unitario con IVA" : "Unitario"}
                    </th>
                    <th className={estilos.derecha} style={{ width: 84 }}>
                      IVA
                    </th>
                    <th className={estilos.derecha} style={{ width: 120 }}>
                      {conIva ? "Subtotal con IVA" : "Subtotal"}
                    </th>
                    <th style={{ width: 44 }} />
                  </tr>
                </thead>

                <tbody>
                  {lineas.map((linea) => (
                    <tr
                      key={linea.id_inventario}
                      className={
                        linea.es_mano_de_obra ? estilos.lineaMo : undefined
                      }
                    >
                      <td>
                        {linea.descripcion}
                        {linea.es_mano_de_obra && (
                          <span className={estilos.marcaMo}>Margen</span>
                        )}
                        {/* Aviso de faltante: la cantidad pedida supera lo que
                            hay en bodega. La mano de obra no tiene stock. */}
                        {(() => {
                          if (enLaCalle || linea.es_mano_de_obra) return null;
                          const stock = stockPorId.get(linea.id_inventario);
                          if (stock == null || stock >= linea.cantidad)
                            return null;
                          return (
                            <span className={estilos.faltante}>
                              {stock > 0
                                ? `Solo hay ${stock}, faltan ${
                                    linea.cantidad - stock
                                  }`
                                : "No hay en bodega"}
                            </span>
                          );
                        })()}
                      </td>

                      <td className={estilos.derecha} data-rotulo="Cantidad">
                        {linea.es_mano_de_obra && linea.unidad === "Global" ? (
                          <span className={estilos.valorFijo}>1</span>
                        ) : (
                          <CampoDeCantidad
                            className={estilos.campoNumero}
                            valor={linea.cantidad}
                            onCambio={(v) =>
                              cambiar(linea.id_inventario, "cantidad", v)
                            }
                            title={
                              linea.es_mano_de_obra
                                ? "Horas de trabajo"
                                : undefined
                            }
                            aria-label={
                              linea.es_mano_de_obra
                                ? "Horas de mano de obra"
                                : `Cantidad de ${linea.descripcion}`
                            }
                          />
                        )}
                      </td>

                      <td className={estilos.derecha} data-rotulo="Unidad">
                        {linea.es_mano_de_obra ? (
                          <span
                            className={estilos.modoMo}
                            role="group"
                            aria-label="Cómo se cotiza la mano de obra"
                          >
                            <button
                              type="button"
                              className={
                                linea.unidad !== "Global"
                                  ? estilos.modoMoActivo
                                  : undefined
                              }
                              aria-pressed={linea.unidad !== "Global"}
                              onClick={() =>
                                modoDeManoDeObra(linea.id_inventario, "horas")
                              }
                            >
                              Horas
                            </button>
                            <button
                              type="button"
                              className={
                                linea.unidad === "Global"
                                  ? estilos.modoMoActivo
                                  : undefined
                              }
                              aria-pressed={linea.unidad === "Global"}
                              onClick={() =>
                                modoDeManoDeObra(linea.id_inventario, "total")
                              }
                            >
                              Total
                            </button>
                          </span>
                        ) : (
                          <input
                            className={estilos.campoNumero}
                            type="text"
                            list="unidades-medida"
                            value={linea.unidad ?? ""}
                            placeholder="Und."
                            onChange={(e) =>
                              cambiarUnidad(linea.id_inventario, e.target.value)
                            }
                            title="Unidad de medida: Und., m…"
                            aria-label={`Unidad de ${linea.descripcion}`}
                          />
                        )}
                      </td>

                      <td
                        className={estilos.derecha}
                        data-rotulo={conIva ? "Unitario con IVA" : "Unitario"}
                      >
                        {/* NUNCA EDITABLE EN MANO DE OBRA. Si el teléfono
                            todavía no tiene la tarifa guardada, se dice que la
                            pone el sistema en vez de abrir el campo: la base
                            la impone al guardar (0225) y un campo abierto
                            solo invitaba a escribir otra cosa. */}
                        {linea.es_mano_de_obra && linea.unidad !== "Global" ? (
                          <span
                            className={estilos.valorFijo}
                            title="La tarifa por hora la pone el sistema: aquí solo se escriben las horas."
                          >
                            {tarifaManoObra > 0
                              ? `${pesos(tarifaManoObra)} / h`
                              : "Tarifa del sistema"}
                          </span>
                        ) : (
                          <CampoDePesos
                            className={`${estilos.campoNumero} ${estilos.campoPrecio}`}
                            valor={
                              conIva
                                ? conIvaDe(linea.precio_unitario, linea.iva)
                                : linea.precio_unitario
                            }
                            onCambio={(v) =>
                              cambiar(
                                linea.id_inventario,
                                "precio_unitario",
                                conIva ? baseDe(v, linea.iva) : v
                              )
                            }
                            title={
                              linea.es_mano_de_obra
                                ? "Valor total de la mano de obra"
                                : undefined
                            }
                            aria-label={
                              linea.es_mano_de_obra
                                ? "Valor total de la mano de obra"
                                : `Precio de ${linea.descripcion}`
                            }
                          />
                        )}
                      </td>

                      {/* EL IVA DEL RENGLÓN (oct 2026): hay cosas que no
                          llevan IVA. Arranca con el del producto en World
                          Office y se cambia aquí. Una cotización sin IVA en
                          ningún renglón no pasa a FEV (0264). */}
                      <td className={estilos.derecha} data-rotulo="IVA">
                        {/* Solo 0, 5 y 19 %: los que acepta la DIAN (0267).
                            Un producto que llega de WO con otra tasa (10 %,
                            7 %…, errores de digitación) se ve marcado y no
                            deja generar hasta escoger una válida. */}
                        {/* 0301: solo mano de obra, grúa, certificaciones,
                            retro, escalera y radicación cambian de IVA; lo
                            demás lleva el de World Office. Si el de WO no es
                            de la DIAN, sí se deja corregir. */}
                        <select
                          className={`${estilos.campoNumero} ${
                            estilos.campoIva
                          } ${
                            ivaValido(linea.iva) ? "" : estilos.campoIvaMalo
                          }`}
                          disabled={
                            !ivaLibre.has(Number(linea.id_inventario)) &&
                            ivaValido(linea.iva)
                          }
                          title={
                            !ivaLibre.has(Number(linea.id_inventario)) &&
                            ivaValido(linea.iva)
                              ? "El IVA de World Office: solo los servicios (mano de obra, grúa, certificaciones…) lo cambian"
                              : undefined
                          }
                          value={String(linea.iva)}
                          onChange={(e) =>
                            cambiar(
                              linea.id_inventario,
                              "iva",
                              Number(e.target.value)
                            )
                          }
                          aria-label={`IVA de ${linea.descripcion}`}
                        >
                          {!ivaValido(linea.iva) && (
                            <option value={String(linea.iva)} disabled>
                              {Math.round(linea.iva * 10000) / 100} % · corregir
                            </option>
                          )}
                          {IVAS_DIAN.map((v) => (
                            <option key={v} value={String(v)}>
                              {v * 100} %
                            </option>
                          ))}
                        </select>
                      </td>

                      <td
                        className={`${estilos.derecha} ${estilos.celdaSubtotal}`}
                        data-rotulo={conIva ? "Subtotal con IVA" : "Subtotal"}
                      >
                        {pesos(
                          linea.cantidad *
                            linea.precio_unitario *
                            (conIva ? 1 + linea.iva : 1)
                        )}
                      </td>

                      <td className={estilos.celdaQuitar}>
                        <button
                          type="button"
                          className={estilos.quitar}
                          onClick={() => quitar(linea.id_inventario)}
                          aria-label={`Quitar ${linea.descripcion}`}
                        >
                          ✕
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className={estilos.pie}>
                <div className={estilos.totales}>
                  <div className={estilos.fila}>
                    <span>Materiales</span>
                    <span>{pesos(totales.materiales)}</span>
                  </div>

                  <div className={estilos.fila}>
                    <span>Mano de obra</span>
                    <span>{pesos(totales.obra)}</span>
                  </div>

                  <div className={`${estilos.fila} ${estilos.base}`}>
                    <span>Base</span>
                    <span>{pesos(totales.base)}</span>
                  </div>

                  <div className={estilos.fila}>
                    <span>IVA</span>
                    <span>{pesos(totales.iva)}</span>
                  </div>

                  <div className={`${estilos.fila} ${estilos.total}`}>
                    <span>Total</span>
                    <span>{pesos(totales.total)}</span>
                  </div>

                  {/* Con cuánto queda la empresa, mientras todavía se puede
                      corregir. En una venta de materiales no aplica. */}
                  {!esLibre && tieneManoDeObra && (
                    <MargenEnVivo
                      porcentaje={totales.porcentajeObra}
                      ordenServicioId={ordenServicioId}
                    />
                  )}

                  {/* No es una sugerencia: sin esta línea la base rechaza la
                      cotización. Una orden de servicio es un trabajo, y un
                      trabajo sin mano de obra no es un trabajo. */}
                  {exigeManoDeObra && !tieneManoDeObra && manoDeObra && (
                    <p className={estilos.faltaMo}>
                      <b>Falta la mano de obra.</b> Toda cotización de una orden
                      de servicio la lleva, y con su valor.{" "}
                      <button
                        type="button"
                        className={estilos.enlaceAviso}
                        onClick={() => agregar(manoDeObra)}
                      >
                        Agregarla
                      </button>
                    </p>
                  )}
                </div>
              </div>
            </>
          )}
        </>
      )}
    </Modal>
  );
}
